import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';

export interface Option {
  value: string;
  label: string;
}

@Component({
  selector: 'app-multi-select-dropdown',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.css'],
})
export class MultiSelectDropdownComponent {
  @Input() options: Option[] = [];
  @Input() placeholder: string = 'Select options';
  // Two-way binding for selected values
  @Input() selectedValues: string[] = [];
  @Output() selectedValuesChange = new EventEmitter<string[]>();
  // Event to handle "Add More" click
  @Output() addMore = new EventEmitter<void>();

  dropdownOpen = false;
  // Panel width, captured once when the dropdown opens (see onToggle). Must NOT
  // be a getter that reads offsetWidth: measuring layout during change detection
  // makes the value oscillate with the page scrollbar, which throws NG0100 and
  // drives an infinite reposition->reflow->CD loop (frozen UI).
  menuWidth = 0;

  // The panel renders in a body-level CDK overlay (not inside the fixed
  // mobile-page-shell), so iOS no longer steals row taps and the panel scrolls
  // natively. Prefer opening downward; fall back to upward when there isn't
  // room below — this replaces the old manual drop-up / max-height logic.
  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 2,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -2,
    },
  ];

  // Inject ElementRef to get a reference to the host element
  constructor(private elementRef: ElementRef) {}

  // Stable identity for *ngFor so option DOM nodes are reused when the options
  // array reference changes but values are unchanged — prevents the iOS "freeze"
  // from continuous teardown/rebuild when [options] is a new array each cycle.
  trackByValue(_i: number, opt: Option): string {
    return opt.value;
  }

  onToggle() {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      // Capture the toggle width once, before the overlay/backdrop can affect
      // the page scrollbar. Bound to [cdkConnectedOverlayWidth] as a stable
      // value so change detection never re-measures layout.
      const toggle = this.elementRef.nativeElement.querySelector(
        '.dropdown-toggle-borders'
      ) as HTMLElement | null;
      this.menuWidth = toggle ? toggle.offsetWidth : 0;
    }
  }

  closeDropdown() {
    this.dropdownOpen = false;
  }

  // Toggle option when clicking the whole dropdown item
  toggleOption(option: Option, event: Event) {
    // Toggle the option only if the click is not coming from the checkbox itself.
    const isSelected = this.selectedValues.includes(option.value); // Using includes for better readability
    if (isSelected) {
      this.selectedValues = this.selectedValues.filter(
        (v) => v !== option.value
      );
    } else {
      this.selectedValues = [...this.selectedValues, option.value]; // Using spread for immutability
    }
    this.selectedValuesChange.emit(this.selectedValues);
    event.preventDefault(); // Prevent default behavior (e.g., if there's a link)
  }

  // Prevent the click on the checkbox from propagating to the parent dropdown item's click handler
  onCheckboxClick(event: Event) {
    event.stopPropagation();
  }

  onCheckboxChange(event: Event, option: Option) {
    const target = event.target as HTMLInputElement; // Cast to HTMLInputElement to access .checked
    const isSelected = this.selectedValues.includes(option.value);

    if (target.checked && !isSelected) {
      this.selectedValues = [...this.selectedValues, option.value];
    } else if (!target.checked && isSelected) {
      this.selectedValues = this.selectedValues.filter(
        (v) => v !== option.value
      );
    }
    this.selectedValuesChange.emit(this.selectedValues);
    // No need to close the dropdown on checkbox change for multi-select
  }

  onAddMore(event: Event) {
    // Stop propagation to prevent immediate re-close
    event.stopPropagation();
    this.addMore.emit();
    this.dropdownOpen = false; // Optionally close the dropdown after "Add More" is clicked
  }
}
